import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  VendorWithRelations,
  VendorCategory,
  VendorUsage,
  CreateVendorInput,
  UpdateVendorInput,
  AddVendorCategoryInput,
  AddVendorContactInput,
} from '@peles/shared';
import * as vendorsApi from '../api/vendors';

export function useVendors(
  filters?: {
    isActive?: boolean;
    city?: string;
    vendorCategoryId?: string;
  },
  options?: { enabled?: boolean },
) {
  return useQuery<VendorWithRelations[]>({
    queryKey: ['vendors', filters],
    queryFn: () => vendorsApi.listVendors(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useVendor(id: string) {
  return useQuery<VendorWithRelations>({
    queryKey: ['vendors', id],
    queryFn: () => vendorsApi.getVendor(id),
    enabled: !!id,
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVendorInput) => vendorsApi.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateVendorInput }) =>
      vendorsApi.updateVendor(id, updates),
    onSuccess: (updatedVendor) => {
      queryClient.setQueryData(['vendors', updatedVendor.id], updatedVendor);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useDeactivateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => vendorsApi.deactivateVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useVendorUsage(id: string, enabled = true) {
  return useQuery<VendorUsage>({
    queryKey: ['vendors', id, 'usage'],
    queryFn: () => vendorsApi.getVendorUsage(id),
    enabled: enabled && !!id,
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reassignToVendorId }: { id: string; reassignToVendorId: string | null }) =>
      vendorsApi.deleteVendor(id, reassignToVendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      // Reassigned records live under other query roots
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['entity-links'] });
    },
  });
}

export function useAddVendorCategory(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddVendorCategoryInput) => vendorsApi.addVendorCategory(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useRemoveVendorCategory(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => vendorsApi.removeVendorCategory(vendorId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useAddVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddVendorContactInput) => vendorsApi.addVendorContact(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useRemoveVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => vendorsApi.removeVendorContact(vendorId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useVendorCategories() {
  return useQuery<VendorCategory[]>({
    queryKey: ['vendor-categories'],
    queryFn: () => vendorsApi.listVendorCategories(),
    staleTime: Infinity,
  });
}
